import React, { ReactElement } from "react";

import { DateDisplay, DateTimeDisplay } from "../../hooks";
import { ensure } from "../../utils/ensure/ensure";
import { depascalize } from "../../utils/string/depascalize";
import { Badge } from "../items/Badge";
import { Icon } from "../items/Icon";
import { Text } from "../items/Text";
import { Stack } from "../layouts/stack/Stack";
import { Link } from "../navigations/Link";
import { TooltipHost } from "../surfaces/tooltip/TooltipHost";
import { Theme } from "../theme/Theme";
import { ListTableColumn } from "./ListTableColumn";

export const renderDefault = <T,>(item?: T, _index?: number, column?: ListTableColumn<T>): ReactElement | null => {
    ensure(item);
    ensure(column);

    const fieldContent = item[column.key];

    return <Stack>{fieldContent}</Stack>;
};

export const renderText = <T,>(item?: T, _index?: number, column?: ListTableColumn<T>): ReactElement | null => {
    ensure(item);
    ensure(column);

    return <Text variant="smallPlus" value={item[column.key]} maxLength={column.maxLenght} />;
};

export const renderTextWithTooltip = <T,>(
    item?: T,
    _index?: number,
    column?: ListTableColumn<T>
): ReactElement | null => {
    ensure(item);
    ensure(column);

    const text = item[column.key];

    return <Text variant="smallPlus" value={text} tooltip={text} maxLength={column.maxLenght} />;
};

export const renderOptionalTextWithTooltip = <T,>(
    item?: T,
    _index?: number,
    column?: ListTableColumn<T>
): ReactElement | null => {
    ensure(item);
    ensure(column);

    const text = item[column?.key];

    if (!text) {
        return <>-</>;
    }

    return <Text variant="smallPlus" value={text} tooltip={text} maxLength={column.maxLenght} />;
};

export const renderDateTime = <T,>(item?: T, _index?: number, column?: ListTableColumn<T>): ReactElement | null => {
    ensure(item);
    ensure(column);

    const fieldContent = item[column.key];

    if (typeof fieldContent !== "string" && !(fieldContent instanceof Date)) {
        throw new Error(`Type of '${column.key}' should be string or Date.`);
    }

    return (
        <Stack>
            <DateTimeDisplay date={fieldContent} />
        </Stack>
    );
};

export const renderOptionalDateTime = <T,>(
    item?: T,
    _index?: number,
    column?: ListTableColumn<T>
): ReactElement | null => {
    ensure(item);
    ensure(column);

    const fieldContent = item[column.key];

    if (!fieldContent) {
        return <Text variant="small">-</Text>;
    }

    return (
        <Stack>
            <DateTimeDisplay date={fieldContent} />
        </Stack>
    );
};

export const renderDate = <T,>(item?: T, _index?: number, column?: ListTableColumn<T>): ReactElement | null => {
    ensure(item);
    ensure(column);

    const fieldContent = item[column.key];

    if (!fieldContent) return null;

    if (typeof fieldContent !== "string" && !(fieldContent instanceof Date)) {
        throw new Error(`Type of '${column.key}' should be string or Date.`);
    }

    return (
        <Stack>
            <DateDisplay date={fieldContent} />
        </Stack>
    );
};

export const renderButtons = <T extends { id?: string }>(
    item?: T,
    _index?: number,
    column?: ListTableColumn<T>
): ReactElement | null => {
    ensure(item);
    ensure(column);

    ensure(column.buttons);

    return (
        <Stack horizontal={true} tokens={{ childrenGap: 2 }}>
            {column.buttons.map((button) => {
                const itemId = button.getItemId ? button.getItemId(item) : item.id;
                ensure(itemId);

                const disabled = button.isDisabled ? button.isDisabled(item) : false;

                const onClick = () => {
                    if (button.onItemInvoked) {
                        button.onItemInvoked(item);
                    }
                };

                return button.render({ id: button.id, disabled, onClick, itemId, item });
            })}
        </Stack>
    );
};

export const renderBooleanIcon = <T,>(item?: T, _index?: number, column?: ListTableColumn<T>): ReactElement | null => {
    ensure(item);
    ensure(column);

    const fieldContent = item[column.key];

    return <Stack horizontalAlign="center">{!!fieldContent && <Icon iconName="Accept" />}</Stack>;
};

export const renderBooleanIconWithTooltip = <T,>(
    item?: T,
    _index?: number,
    column?: ListTableColumn<T>,
    tooltipMessages?: string[]
): ReactElement | null => {
    ensure(item);
    ensure(column);

    const fieldContent = item[column.key];

    const getTooltipContent = (messages?: string[]) => {
        return (
            <Stack tokens={{ childrenGap: 5 }}>
                {messages?.map((message: string) => {
                    return <Stack key={message}>{message}</Stack>;
                })}
            </Stack>
        );
    };

    return (
        <TooltipHost content={getTooltipContent(tooltipMessages)}>
            <Stack horizontalAlign="center">{!!fieldContent && <Icon iconName="Accept" />}</Stack>
        </TooltipHost>
    );
};

export const renderLink = <T,>(item?: T, _index?: number, column?: ListTableColumn<T>): ReactElement | null => {
    ensure(item);
    ensure(column);
    ensure(column.getHref);

    const fieldContent = item[column.key];
    const isDisabled = column.isDisabled ? column.isDisabled(item) : false;
    const isAnnotated = column.isAnnotated ? column.isAnnotated(item) : false;
    const target = column.openInNewTab ? "_blank" : "_self";
    const href = column.getHref(item);
    const defaultFieldContent = <Icon iconName="PageLink" />;
    const tooltipText = column.getTooltipText && column.getTooltipText(item);

    if (!href) {
        return tooltipText ? renderTextWithTooltip(item, _index, column) : renderText(item, _index, column);
    }

    const link = (
        <Link
            key={column.key.toString()}
            href={`#${href}`}
            target={target}
            styles={{ root: { color: Theme.palette.neutralPrimary, paddingRight: 10 } }}
            disabled={isDisabled}
        >
            {fieldContent ?? defaultFieldContent}
        </Link>
    );

    const linkWithTooltip = tooltipText ? <TooltipHost content={tooltipText}>{link}</TooltipHost> : link;

    return (
        <Stack verticalAlign="start" horizontal={true}>
            {linkWithTooltip}
            {isAnnotated && renderAnnotation(isAnnotated)}
        </Stack>
    );
};

export const renderClickableLink = <T,>(
    item?: T,
    _index?: number,
    column?: ListTableColumn<T>
): ReactElement | null => {
    ensure(item);
    ensure(column);
    ensure(column.onItemInvoked);

    const { onItemInvoked } = column;

    const fieldContent = item[column.key];
    const isDisabled = column.isDisabled ? column.isDisabled(item) : false;
    const isAnnotated = column.isAnnotated ? column.isAnnotated(item) : false;
    const onClick = () => onItemInvoked(item);

    return (
        <Stack verticalAlign="start" horizontal={true}>
            <Link
                key={column.key.toString()}
                onClick={onClick}
                styles={{ root: { color: Theme.palette.neutralPrimary, paddingRight: 10 } }}
                disabled={isDisabled}
            >
                {fieldContent}
            </Link>
            {isAnnotated && renderAnnotation(isAnnotated)}
        </Stack>
    );
};

export const renderTextWithAnnotation = <T,>(
    item?: T,
    _index?: number,
    column?: ListTableColumn<T>
): ReactElement | null => {
    ensure(item);
    ensure(column);

    const fieldContent = item[column.key];

    const isAnnotated = column.isAnnotated ? column.isAnnotated(item) : false;

    return (
        <Stack horizontal={true}>
            <Stack styles={{ root: { paddingRight: 10 } }}>{fieldContent}</Stack>
            {isAnnotated && renderAnnotation(isAnnotated)}
        </Stack>
    );
};

export const renderEnumValue = <T,>(
    enumType: Record<number, string>,
    item?: T,
    _index?: number,
    column?: ListTableColumn<T>
): ReactElement | null => {
    ensure(item);
    ensure(column);

    const fieldContent = item[column.key];

    if (Array.isArray(fieldContent)) {
        return (
            <>
                {fieldContent
                    .map((field) => {
                        return depascalize(enumType[field]);
                    })
                    .join(", ")}
            </>
        );
    }

    return <>{depascalize(enumType[fieldContent])}</>;
};

export const renderCodes = <T,>(item?: T, _index?: number, column?: ListTableColumn<T>): ReactElement | null => {
    ensure(item);
    ensure(column);

    const codedObjects = item[column.key] as { code: string }[];

    ensure(codedObjects);

    return (
        <Stack tokens={{ childrenGap: 5 }}>
            {codedObjects.map(({ code }) => (
                <Text key={code} variant="smallPlus" value={code} maxLength={column.maxLenght} />
            ))}
        </Stack>
    );
};

export const renderNames = <T,>(item?: T, _index?: number, column?: ListTableColumn<T>): ReactElement | null => {
    ensure(item);
    ensure(column);

    const namedObjects = item[column.key] as { name: string }[];

    ensure(namedObjects);

    return (
        <Stack tokens={{ childrenGap: 5 }}>
            {namedObjects.map(({ name }) => (
                <Text key={name} variant="smallPlus" value={name} maxLength={column.maxLenght} />
            ))}
        </Stack>
    );
};

export const renderJoined = <T,>(item?: T, _index?: number, column?: ListTableColumn<T>): ReactElement | null => {
    ensure(item);
    ensure(column);

    const stringArray = item[column.key] as string[];

    ensure(stringArray);

    return (
        <Stack tokens={{ childrenGap: 5 }}>
            {stringArray.map((text: string) => (
                <Text key={text} variant="smallPlus" value={text} maxLength={column.maxLenght} />
            ))}
        </Stack>
    );
};

export const renderName = <T,>(item?: T, _index?: number, column?: ListTableColumn<T>): ReactElement | null => {
    ensure(item);
    ensure(column);

    const fieldContent = item[column.key];

    if (!fieldContent.hasOwnProperty("name")) {
        throw new Error(`${column.key} is missing name property.`);
    }

    const name = fieldContent.name;

    return <>{name}</>;
};

export const renderOptionalName = <T,>(item?: T, index?: number, column?: ListTableColumn<T>): ReactElement | null => {
    ensure(item);
    ensure(column);

    const property = item[column?.key];

    if (!property) {
        return <>-</>;
    }

    return renderName(item, index, column);
};

const renderAnnotation = (annotation: string | true): ReactElement => {
    const text = typeof annotation === "string" ? annotation : "modified";

    return <Badge text={text} />;
};
